/**
 * «Uso en el Despacho» (biblioteca.html, sección de arriba de la columna;
 * ADR-20 pt 4): los contadores SIN CASA que el asesor suma al escribir cada
 * traza — sin Engagement, sin texto, sin identidad. Dicen a Enric qué
 * escribir (los métodos que más responden sin ficha citada) y a los socios
 * qué firmar primero (las fichas más citadas).
 *
 * - dos cifras: turnos con biblioteca y «sin ficha citada» (cifra y %);
 * - «Métodos con más respuestas sin ficha» (top 5, «n de m») y «Fichas más
 *   citadas» (top 8, con el código de su cuaderno). Tocar un método abre su
 *   Skill; tocar una ficha, la ficha;
 * - por método y por ficha solo llegan si la API los enseña
 *   (`porFichaVisible`: rol completo, y en producción cinco casas o más);
 *   si no, una línea de porqué en su lugar;
 * - vacío honesto con todo a cero; cargando; error que se lee y no
 *   bloquea (la biblioteca de al lado sigue viva). `?uso=` fuerza el estado
 *   (datos · vacio · oculto · sinpermiso · cargando · error).
 *
 * Recibe el cliente por props (`api`): la página lo ata al real y los
 * tests a uno falso, sin MSAL.
 */
import { useNavigate } from "@tanstack/react-router";
import type { BibliotecaTelemetria } from "@skein/contracts";
import { Button, Card, CardHeader, CardPad, Empty, Fainted, Feed, FeedItem, Skeleton, SidepaneSection, StatCell, StatGrid, Vh } from "@skein/ui";
import { useTelemetriaDe, type TelemetriaApi } from "../../api/biblioteca";
import { sinGuiones } from "./logica";

export const TOP_METODOS = 5;
export const TOP_FICHAS = 8;

/** Porcentaje entero de «sin ficha citada» sobre los turnos; sin turnos, null. */
export const pctSinFicha = (t: Pick<BibliotecaTelemetria, "turnos" | "sinFicha">): number | null =>
  t.turnos > 0 ? Math.round((t.sinFicha / t.turnos) * 100) : null;

/** Los métodos que más responden sin ficha: solo los que tienen alguna, de más a menos (empate: más turnos). */
export const metodosSinFicha = (t: BibliotecaTelemetria) =>
  t.porSkill.filter((s) => s.sinFicha > 0).sort((a, b) => b.sinFicha - a.sinFicha || b.turnos - a.turnos || a.slug.localeCompare(b.slug)).slice(0, TOP_METODOS);

/** Las más citadas, ya ordenadas por la API; aquí solo se corta. */
export const fichasCitadas = (t: BibliotecaTelemetria) => t.masCitadas.filter((f) => f.veces > 0).slice(0, TOP_FICHAS);

/** Lo que pinta la sección. `oculto`: hay turnos, pero por método y por ficha no se enseñan aquí. */
export type VistaUso = "cargando" | "error" | "vacio" | "oculto" | "datos";
/** `?uso=sinpermiso`: el oculto de un socio (rol de biblioteca), para revisarlo con el rol completo. */
const FORZABLES = ["cargando", "error", "vacio", "oculto", "datos"] as const;
export function vistaUso(q: { data?: BibliotecaTelemetria | undefined; isPending: boolean; isError: boolean }, forzada?: string): VistaUso {
  if (forzada === "sinpermiso") return "oculto";
  if ((FORZABLES as readonly string[]).includes(forzada ?? "")) return forzada as VistaUso;
  if (q.data) return q.data.turnos === 0 ? "vacio" : q.data.porFichaVisible ? "datos" : "oculto";
  return q.isError ? "error" : "cargando";
}

/** El porqué de no enseñar por método y por ficha: con el rol de biblioteca, por el rol; si no, por las casas. */
export const porqueOculto = (rol: "completo" | "biblioteca" | undefined): { title: string; body: string } =>
  rol === "biblioteca"
    ? { title: "Por método y por ficha, solo el equipo de Skein.", body: "Con pocas casas, estos contadores identificarían al cliente: los ve quien tiene el acceso completo del Studio." }
    : { title: "Por método y por ficha, todavía no.", body: "Se enseña cuando haya al menos cinco casas: con una o dos, identificarían al cliente." };

function Cifras({ t }: { t: BibliotecaTelemetria }) {
  const pct = pctSinFicha(t);
  return (
    <StatGrid>
      <StatCell n={t.turnos} label="turnos con biblioteca" />
      <StatCell n={t.sinFicha} label={pct === null ? "sin ficha citada" : `sin ficha citada · ${pct} %`} />
    </StatGrid>
  );
}

/** `cuadernos`: código → título, para que el lector oiga el cuaderno entero (a la vista solo va el código). */
export function UsoDespacho({ api, rol, forzada, cuadernos }: { api: TelemetriaApi | undefined; rol?: "completo" | "biblioteca" | undefined; forzada?: string | undefined; cuadernos?: ReadonlyMap<string, string> | undefined }) {
  const q = useTelemetriaDe(api);
  const navigate = useNavigate();
  const view = vistaUso(q, forzada);
  const rolVisto = forzada === "sinpermiso" ? "biblioteca" : rol;
  const t = q.data;

  let body;
  if (view === "cargando" || ((view === "datos" || view === "oculto") && !t)) {
    body = (
      <Card flush aria-busy="true">
        <StatGrid>
          <StatCell n={<Skeleton style={{ height: "1.4em", width: "3rem" }} />} label={<Skeleton style={{ height: ".8em", width: "7rem" }} />} />
          <StatCell n={<Skeleton style={{ height: "1.4em", width: "2.5rem" }} />} label={<Skeleton style={{ height: ".8em", width: "6rem" }} />} />
        </StatGrid>
        <CardPad>
          <Skeleton style={{ height: ".9em", width: "80%" }} />
          <Skeleton style={{ height: ".9em", width: "60%", marginTop: "var(--sk-sp-3)" }} />
          <Skeleton style={{ height: ".9em", width: "70%", marginTop: "var(--sk-sp-3)" }} />
        </CardPad>
      </Card>
    );
  } else if (view === "error") {
    body = (
      <Empty inline role="status" title="No llego a los contadores de uso.">
        La biblioteca que ves es real; estos números, no los tengo. Reintento solo cada 30 s — <Button variant="link" onClick={() => void q.refetch()}>reintentar ahora</Button>.
      </Empty>
    );
  } else if (view === "vacio" || !t) {
    body = <Empty inline title="Todavía ningún turno con biblioteca.">Cuando el asesor responda con un método de Skein, aquí se cuenta cuántas veces citó una ficha y cuántas no.</Empty>;
  } else if (view === "oculto") {
    body = (
      <Card flush>
        <Cifras t={t} />
        <CardPad><Empty inline title={porqueOculto(rolVisto).title}>{porqueOculto(rolVisto).body}</Empty></CardPad>
      </Card>
    );
  } else {
    const metodos = metodosSinFicha(t);
    const fichas = fichasCitadas(t);
    body = (
      <Card flush>
        <Cifras t={t} />
        <CardHeader title="Métodos con más respuestas sin ficha" />
        {metodos.length > 0
          ? (
            <Feed full clock>
              {metodos.map((s) => (
                <FeedItem key={s.slug} t={`${s.sinFicha} de ${s.turnos}`} onClick={() => void navigate({ to: "/skills/$slug", params: { slug: s.slug } })}>
                  <b>{s.title ?? sinGuiones(s.slug)}</b><Vh> — {s.sinFicha} respuestas sin ficha de {s.turnos}</Vh>
                </FeedItem>
              ))}
            </Feed>
          )
          : <CardPad><Empty inline title="Ningún método respondió sin ficha.">Cada respuesta con método citó al menos una ficha.</Empty></CardPad>}
        <CardHeader title="Fichas más citadas" />
        {fichas.length > 0
          ? (
            <Feed full clock>
              {fichas.map((f) => (
                <FeedItem key={f.slug} t={`${f.veces} ${f.veces === 1 ? "vez" : "veces"}`} onClick={() => void navigate({ to: "/conocimiento/$slug", params: { slug: f.slug } })}>
                  <b>{f.title ?? sinGuiones(f.slug)}</b>{f.notebook && <> <Fainted className="sk-mono">{f.notebook}</Fainted></>}
                  <Vh> — {f.notebook ? `${f.notebook}${cuadernos?.get(f.notebook) ? ` · ${cuadernos.get(f.notebook)}` : ""}, ` : ""}citada {f.veces} {f.veces === 1 ? "vez" : "veces"}</Vh>
                </FeedItem>
              ))}
            </Feed>
          )
          : <CardPad><Empty inline title="Ninguna ficha citada todavía.">El asesor ha usado métodos, pero aún no ha citado ninguna ficha.</Empty></CardPad>}
      </Card>
    );
  }

  return <SidepaneSection title="Uso en el Despacho">{body}</SidepaneSection>;
}
